import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./SegmentationSection.css";

// Operator-specific capabilities, primary action label and route per segment.
// `kind` distinguishes product segments (verification & certification) from
// service/equipment segments (audit & calibration).
const SEGMENTS = [
  {
    id: "gold",
    name: "Gold",
    description: "24K, 22K, 18K gold jewellery with BIS hallmarking",
    icon: "🪙",
    color: "var(--gold-400, var(--brand-gold-400))",
    kind: "product",
    operator: {
      capabilities: ["BIS Hallmarking", "Purity Assay", "Weight Audit"],
      action: "Verify Quality",
    },
  },
  {
    id: "silver",
    name: "Silver",
    description: "999 fine silver, 925 sterling silver jewellery",
    icon: "⚪",
    color: "#cbd5e1",
    kind: "product",
    operator: {
      capabilities: ["999 Purity Check", "925 Assay", "Tarnish Test"],
      action: "Verify Quality",
    },
  },
  {
    id: "platinum",
    name: "Platinum",
    description: "Pt 950 high purity platinum creations",
    icon: "🔷",
    color: "#7dd3fc",
    kind: "product",
    operator: {
      capabilities: ["Pt 950 Assay", "Purity Verification", "Density Test"],
      action: "Verify Quality",
    },
  },
  {
    id: "diamond",
    name: "Diamond",
    description: "Natural diamonds with IGI/GIA certification",
    icon: "💎",
    color: "#a5b4fc",
    kind: "product",
    operator: {
      capabilities: ["IGI/GIA Verify", "4C Grading", "Laser Inscription"],
      action: "Verify Quality",
    },
  },
  {
    id: "gem",
    name: "GEM",
    description: "Precious gemstones: Ruby, Emerald, Sapphire",
    icon: "💠",
    color: "#5eead4",
    kind: "product",
    operator: {
      capabilities: ["Gem Grading", "Origin Certificate", "Treatment Check"],
      action: "Verify Quality",
    },
  },
  {
    id: "pearl",
    name: "PEARL",
    description: "Freshwater, South Sea & Basra cultured pearls",
    icon: "⚪",
    color: "#fbcfe8",
    kind: "product",
    operator: {
      capabilities: ["Luster Grading", "Size Sort", "Authenticity Test"],
      action: "Verify Quality",
    },
  },
  {
    id: "bandhel",
    name: "BANDHEL",
    description: "Heritage craft & wirework jewellery",
    icon: "🔗",
    color: "#fcd34d",
    kind: "product",
    operator: {
      capabilities: ["Craft Audit", "Wirework QC", "Heritage Cert"],
      action: "Verify Quality",
    },
  },
  {
    id: "decor",
    name: "DECOR",
    description: "Showroom displays, trays & packaging materials",
    icon: "📦",
    color: "#d8b4fe",
    kind: "service",
    operator: {
      capabilities: ["Display QC", "Packaging Audit", "Material Spec"],
      action: "Audit Quality",
    },
  },
  {
    id: "brand",
    name: "BRAND",
    description: "Jewellery branding, packaging & marketing services",
    icon: "🏷️",
    color: "#fdba74",
    kind: "service",
    operator: {
      capabilities: ["Brand Compliance", "Marketing Audit", "Asset Review"],
      action: "Review Brand",
    },
  },
  {
    id: "tool",
    name: "TOOL",
    description:
      "Jewellery tools: Bench tools, polishing, assaying equipment",
    icon: "🔧",
    color: "#f59e0b",
    kind: "equipment",
    operator: {
      capabilities: ["Tool Calibration", "Assay Cert", "Equipment QC"],
      action: "Calibrate Tools",
    },
  },
  {
    id: "machine",
    name: "MACHINE",
    description: "Jewellery machinery: CNC, laser, casting equipment",
    icon: "⚙️",
    color: "#94a3b8",
    kind: "equipment",
    operator: {
      capabilities: ["Machine Inspection", "CNC Calibration", "Safety Cert"],
      action: "Inspect Machine",
    },
  },
];

const TABS = [
  { id: "buyer", label: "Buyer", icon: "🛍️", route: "/buyer" },
  { id: "seller", label: "Seller", icon: "💎", route: "/seller" },
  { id: "operator", label: "Operator", icon: "⚡", route: "/operator" },
];

const SegmentationSection = () => {
  const [activeTab, setActiveTab] = useState("buyer");

  const activeRoute =
    TABS.find((t) => t.id === activeTab)?.route || "/operator";

  return (
    <section className="segmentation-section" id="segmentation">
      <div className="jmi-container">
        <div className="section-header-center">
          <div className="section-label">JMI BUSINESS SEGMENTATION</div>
          <h2 className="section-title">
            Specialized <span className="gold-gradient-text">Segments</span> for
            Targeted Solutions
          </h2>
          <p className="section-subtitle">
            Tailored business segments addressing specific needs of buyers,
            sellers, and operators within the JMI B2B jewellery network
            ecosystem.
          </p>
        </div>

        {/* Role Selection Tabs */}
        <div className="role-selector-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              className={`role-tab-btn ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              aria-pressed={activeTab === tab.id}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Operator highlight bar — only in operator mode */}
        {activeTab === "operator" && (
          <div className="operator-highlight-bar" role="region" aria-label="Operator capabilities overview">
            <div className="operator-highlight-item">
              <span className="operator-highlight-icon">🛡️</span>
              <div>
                <strong>Quality Verify</strong>
                <span>Hallmarking · Assay · Certification</span>
              </div>
            </div>
            <div className="operator-highlight-divider" />
            <div className="operator-highlight-item">
              <span className="operator-highlight-icon">📐</span>
              <div>
                <strong>Calibration</strong>
                <span>Tools & machinery inspection</span>
              </div>
            </div>
            <div className="operator-highlight-divider" />
            <div className="operator-highlight-item">
              <span className="operator-highlight-icon">📜</span>
              <div>
                <strong>Audit & Cert</strong>
                <span>Brands · Decor · Heritage craft</span>
              </div>
            </div>
          </div>
        )}

        {/* Segmentation Grid */}
        <div className="segment-cards-grid">
          {SEGMENTS.map((segment) => {
            const op = segment.operator;
            return (
              <div
                key={segment.id}
                className={`segment-card ${activeTab === "operator" ? "segment-card--operator" : ""}`}
                style={{ "--segment-accent": segment.color }}
              >
                <div className="segment-card-accent-bar" />
                <div className="segment-icon">{segment.icon}</div>
                <h4 className="segment-title">{segment.name}</h4>
                <p className="segment-description">{segment.description}</p>

                {/* Operator capabilities badges */}
                {activeTab === "operator" && op && (
                  <ul
                    className="segment-operator-caps"
                    aria-label={`${segment.name} operator capabilities`}
                  >
                    {op.capabilities.map((cap) => (
                      <li key={cap} className="segment-cap-tag">
                        <span className="segment-cap-dot" aria-hidden="true" />
                        {cap}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Segment kind pill — only in operator mode */}
                {activeTab === "operator" && (
                  <span className={`segment-kind-pill segment-kind-${segment.kind}`}>
                    {segment.kind === "product" && "Verify"}
                    {segment.kind === "service" && "Audit"}
                    {segment.kind === "equipment" && "Calibrate"}
                  </span>
                )}

                <div className="segment-actions">
                  {activeTab === "buyer" && (
                    <Link to="/buyer" className="btn btn-sm btn-outline">
                      Source Products
                    </Link>
                  )}
                  {activeTab === "seller" && (
                    <Link to="/seller" className="btn btn-sm btn-outline-gold">
                      List Inventory
                    </Link>
                  )}
                  {activeTab === "operator" && (
                    <Link
                      to="/operator"
                      className="btn btn-sm btn-secondary"
                    >
                      <span aria-hidden="true">✓</span>
                      {op ? op.action : "Verify Quality"}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}

          {/* Tech Segment — Buyers only */}
          {activeTab === "buyer" && (
            <div
              className="segment-card segment-card--tech"
              style={{ "--segment-accent": "var(--brand-gold-400)" }}
            >
              <div className="segment-card-accent-bar" />
              <div className="segment-icon">💻</div>
              <h4 className="segment-title">Tech</h4>
              <p className="segment-description">
                ERP ,POS ,Digital Marketing , E-Commerce ,CRM ,Analytics & BI
              </p>
              <div className="segment-actions">
                <Link to="/buyer" className="btn btn-sm btn-outline">
                  Explore Solutions
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="segmentation-cta">
          <div className="segmentation-cta-content">
            <h3>Ready to Access Your Specialized Segment?</h3>
            <p>
              Whether you're a buyer seeking specific jewellery categories, a
              seller looking to expand your market reach, or an operator aiming
              to provide specialized services - JMI has dedicated workflows for
              your exact needs.
            </p>
            <div className="segmentation-cta-actions">
              <Link to={activeRoute} className="btn btn-primary btn-lg">
                {activeTab === "buyer"
                  ? "Enter Buyer Portal"
                  : activeTab === "seller"
                    ? "Join as Seller"
                    : "Access Operator Hub"}
                <span className="arrow-icon">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SegmentationSection;
